import { useState } from 'react';
import { Button, Card, CardBody, cn } from "@heroui/react";
import { X, ArrowRight, CheckCircle } from "lucide-react";
import { useWhatsAppLink } from "@src/presentation/contexts/WhatsAppLinkContext";

/**
 * WhatsApp Icon component for consistent styling
 */
const WhatsAppIcon = ({ className }: { className?: string }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
    </svg>
);

interface WhatsAppLinkBannerProps {
    /**
     * Additional CSS classes for the banner container
     */
    className?: string;
    /**
     * Variant controls the visual style of the banner
     * - "prominent": Full-width gradient banner for high visibility
     * - "compact": Smaller inline banner for sidebars or secondary placement
     * - "minimal": Simple text with button, minimal styling
     */
    variant?: 'prominent' | 'compact' | 'minimal';
    /**
     * Whether the banner can be dismissed
     */
    dismissible?: boolean;
    /**
     * Storage key for dismiss state persistence
     */
    dismissKey?: string;
}

/**
 * WhatsAppLinkBanner Component
 * 
 * A prominent CTA component that encourages users to link their WhatsApp account.
 * Shows only when:
 * - User is authenticated
 * - User hasn't linked their WhatsApp account yet
 * - Banner hasn't been dismissed (if dismissible)
 * 
 * @example
 * // Prominent banner for home page
 * <WhatsAppLinkBanner variant="prominent" />
 * 
 * // Compact banner for sidebar
 * <WhatsAppLinkBanner variant="compact" dismissible />
 * 
 * // Minimal inline CTA
 * <WhatsAppLinkBanner variant="minimal" />
 */
export function WhatsAppLinkBanner({
    className,
    variant = 'prominent',
    dismissible = false,
    dismissKey = 'whatsapp-banner-dismissed',
}: WhatsAppLinkBannerProps) {
    const { openLinkModal, needsLinking, isLinked } = useWhatsAppLink();
    const [isDismissed, setIsDismissed] = useState(() => {
        if (!dismissible) return false;
        return localStorage.getItem(dismissKey) === 'true';
    });

    // Don't render if:
    // - User has already linked WhatsApp
    // - User doesn't need linking (not authenticated)
    // - Banner has been dismissed
    if (isLinked || !needsLinking || isDismissed) {
        return null;
    }

    const handleDismiss = () => {
        setIsDismissed(true);
        if (dismissible) {
            localStorage.setItem(dismissKey, 'true');
        }
    };

    // Render based on variant
    if (variant === 'prominent') {
        return (
            <Card className={cn(
                "border border-[#25D366]/20 bg-[#25D366]/5 dark:bg-[#25D366]/10 rounded shadow-none",
                className
            )}>
                <CardBody className="p-4">
                    <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="flex-shrink-0">
                                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#25D366]/10">
                                    <WhatsAppIcon className="h-5 w-5 text-[#25D366]" />
                                </div>
                            </div>
                            <div className="flex-1 min-w-0">
                                <h3 className="text-sm font-semibold text-[#25D366]">
                                    Link WhatsApp
                                </h3>
                                <p className="text-xs text-default-600">
                                    Access PASCOs directly in WhatsApp
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                            <Button
                                size="sm"
                                onPress={openLinkModal}
                                className="rounded bg-[#25D366] hover:bg-[#20BA5A] text-white"
                                endContent={<ArrowRight className="h-3 w-3" />}
                            >
                                Link
                            </Button>
                            {dismissible && (
                                <Button
                                    isIconOnly
                                    variant="light"
                                    size="sm"
                                    onPress={handleDismiss}
                                    className="rounded text-default-400 hover:text-default-600"
                                    aria-label="Dismiss"
                                >
                                    <X className="h-4 w-4" />
                                </Button>
                            )}
                        </div>
                    </div>
                </CardBody>
            </Card>
        );
    }

    if (variant === 'compact') {
        return (
            <Card className={cn(
                "border border-default-200 rounded shadow-none hover:border-[#25D366]/30 transition-all",
                className
            )}>
                <CardBody className="p-3">
                    <div className="flex items-center gap-3">
                        <div className="flex-shrink-0">
                            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#25D366]/10">
                                <WhatsAppIcon className="h-4 w-4 text-[#25D366]" />
                            </div>
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-foreground truncate">
                                Link WhatsApp
                            </p>
                            <p className="text-xs text-default-500 truncate">
                                Quick access to PASCOs
                            </p>
                        </div>
                        <Button
                            size="sm"
                            variant="flat"
                            onPress={openLinkModal}
                            className="rounded bg-[#25D366]/10 text-[#25D366] hover:bg-[#25D366]/20 flex-shrink-0"
                        >
                            Link
                        </Button>
                    </div>
                    {dismissible && (
                        <button
                            onClick={handleDismiss}
                            className="absolute top-1.5 right-1.5 p-1 rounded hover:bg-default-100 text-default-400 hover:text-default-600 transition-colors"
                            aria-label="Dismiss"
                        >
                            <X className="h-3 w-3" />
                        </button>
                    )}
                </CardBody>
            </Card>
        );
    }

    // Minimal variant
    return (
        <button
            onClick={openLinkModal}
            className={cn(
                "group flex items-center gap-2 py-2 px-3",
                "text-sm text-[#25D366] hover:text-[#20BA5A]",
                "hover:bg-[#25D366]/5 rounded transition-colors",
                className
            )}
        >
            <WhatsAppIcon className="h-4 w-4" />
            <span className="font-medium">Link WhatsApp</span>
            <ArrowRight className="h-3 w-3 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
        </button>
    );
}

/**
 * WhatsApp Linked Status Badge
 * Shows a success indicator when WhatsApp is linked
 */
export function WhatsAppLinkedBadge({ className }: { className?: string }) {
    const { isLinked } = useWhatsAppLink();

    // Only show if user is authenticated and linked
    if (!isLinked) {
        return null;
    }

    return (
        <div
            className={cn(
                "inline-flex items-center gap-1.5 py-1 px-2.5",
                "bg-success/10 text-success rounded",
                "text-xs font-medium",
                className
            )}
        >
            <CheckCircle className="h-3 w-3" />
            <span>WhatsApp Linked</span>
        </div>
    );
}

export default WhatsAppLinkBanner;
